import React from 'react';
import { Plus } from 'lucide-react';

/**
 * ListPageHeader - Reusable premium header component for list pages
 * 
 * Props:
 * - title: string - Page title (e.g., "Sales Orders")
 * - stats: Array of { icon: LucideIcon, label: string, value: string|number, color: 'slate'|'amber'|'blue'|'emerald'|'purple'|'rose'|'violet' }
 * - ctaLabel: string - CTA button label (e.g., "New Sales Order")
 * - onCtaClick: function - CTA button click handler
 * - ctaIcon: LucideIcon - Optional custom icon for CTA (default: Plus)
 * - ctaVariant: 'primary'|'destructive' - CTA button variant
 * - secondaryAction: { label: string, icon: LucideIcon, onClick: function } - Optional secondary action button
 * - extraControls: React.Node - Additional controls to render (e.g., staff filter)
 */
const ListPageHeader = ({ title, stats = [], ctaLabel, onCtaClick, ctaIcon: CtaIcon = Plus, ctaVariant = 'primary', secondaryAction, extraControls }) => {
  const getStatColors = (color) => {
    const colors = {
      slate: { bg: 'bg-white/60', border: 'border-white/80', icon: 'text-slate-400', value: 'text-slate-700' },
      amber: { bg: 'bg-amber-50/60', border: 'border-amber-200/30', icon: 'text-amber-500', value: 'text-amber-600' },
      blue: { bg: 'bg-blue-50/60', border: 'border-blue-200/30', icon: 'text-blue-500', value: 'text-blue-600' },
      emerald: { bg: 'bg-emerald-50/60', border: 'border-emerald-200/30', icon: 'text-emerald-500', value: 'text-emerald-600' },
      purple: { bg: 'bg-purple-50/60', border: 'border-purple-200/30', icon: 'text-purple-500', value: 'text-purple-600' },
      rose: { bg: 'bg-rose-50/60', border: 'border-rose-200/30', icon: 'text-rose-500', value: 'text-rose-600' },
      violet: { bg: 'bg-violet-50/60', border: 'border-violet-200/30', icon: 'text-violet-500', value: 'text-violet-600' },
      teal: { bg: 'bg-teal-50/60', border: 'border-teal-200/30', icon: 'text-teal-500', value: 'text-teal-600' },
    };
    return colors[color] || colors.slate;
  };

  return (
    <div className="
      relative overflow-hidden flex-shrink-0
      bg-white
      border-b border-slate-200/50
    ">
      <div className="relative z-10 px-6 py-4">
        <div className="flex items-center gap-3 flex-wrap">
          {/* Page Title */}
          <h1 className="text-[17px] font-medium text-slate-700 tracking-tight mr-2">{title}</h1>
          
          {/* Stats Pills */}
          {stats.map((stat, index) => {
            const colors = getStatColors(stat.color);
            const Icon = stat.icon;
            return (
              <div 
                key={index}
                className={`inline-flex items-center gap-2 px-3 py-1.5 ${colors.bg} border ${colors.border} rounded-lg backdrop-blur-sm shadow-sm`}
              >
                <Icon className={`w-3.5 h-3.5 ${colors.icon}`} strokeWidth={1.5} />
                <div className="flex flex-col">
                  <span className="text-[9px] uppercase tracking-wider text-slate-400 font-medium leading-none">{stat.label}</span>
                  <span className={`text-[13px] font-semibold ${colors.value} leading-tight`}>{stat.value}</span>
                </div>
              </div>
            );
          })}

          {/* Flexible spacer */}
          <div className="flex-1" />

          {/* Extra Controls (e.g., Staff Filter) */}
          {extraControls}

          {/* Secondary Action Button */}
          {secondaryAction && (
            <button
              onClick={secondaryAction.onClick}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-all text-[12px] font-medium shadow-sm"
            >
              {secondaryAction.icon && <secondaryAction.icon className="w-4 h-4" strokeWidth={2} />}
              {secondaryAction.label}
            </button>
          )}

          {/* CTA Button */}
          {ctaLabel && onCtaClick && (
            <button
              onClick={onCtaClick}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-all text-[12px] font-medium shadow-sm ${
                ctaVariant === 'destructive' 
                  ? 'bg-rose-600 text-white hover:bg-rose-700' 
                  : 'bg-teal-600 text-white hover:bg-teal-700'
              }`}
              data-testid="cta-button"
            >
              <CtaIcon className="w-4 h-4" strokeWidth={2} />
              {ctaLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ListPageHeader;
